import { useState, useCallback } from 'react';
import { Editor, Transforms } from 'slate';
import { useDeleteTag } from 'app/api/tag-queries/tags';
import { TagWithMetadata } from 'app/api/tag-queries';
import { removeTag, isTag } from 'app/slate/extensions/tag';
import { removeBlockTagBinding, findBlockCarrierForTag } from 'app/slate/extensions/tag/utils/blockTagging';
import { useEditorDispatch, useEditorSelector } from 'app/state/contexts/EditorContext';
import { setReferenceData } from 'app/state/redux/documentSlice';

interface UseDeleteTagWithConfirmationOptions {
  editor: Editor;
  documentSuperId?: number;
  onDeleted?: () => void;
}

interface DeleteTagState {
  showModal: boolean;
  pendingTagId: string | null;
  pendingTag: TagWithMetadata | null;
  /** When true, remove the tag node entirely (including text content) instead of unwrapping */
  removeCompletely: boolean;
}

export function useDeleteTagWithConfirmation({ editor, documentSuperId, onDeleted }: UseDeleteTagWithConfirmationOptions) {
  const [state, setState] = useState<DeleteTagState>({
    showModal: false,
    pendingTagId: null,
    pendingTag: null,
    removeCompletely: false,
  });

  const deleteTagMutation = useDeleteTag();
  const editorDispatch = useEditorDispatch();
  const references = useEditorSelector((editor: any) => editor.references);

  const requestDelete = useCallback((tag: TagWithMetadata) => {
    setState({
      showModal: true,
      pendingTagId: tag.id,
      pendingTag: tag,
      removeCompletely: false,
    });
  }, []);

  const requestDeleteById = useCallback((tagId: string) => {
    setState({
      showModal: true,
      pendingTagId: tagId,
      pendingTag: null,
      removeCompletely: true,
    });
  }, []);

  const confirmDelete = useCallback(() => {
    const { pendingTagId, pendingTag, removeCompletely } = state;
    if (!pendingTagId) return;

    const blockEntry = findBlockCarrierForTag(editor, pendingTagId);
    const isBlockTag = !!blockEntry;
    const isTableTag = !isBlockTag && !!pendingTag?.metadata?.tableName;

    if (isBlockTag) {
      removeBlockTagBinding(editor, blockEntry![1], pendingTagId);
    } else if (!isTableTag) {
      if (removeCompletely) {
        // Remove tag node entirely (tag + text content) — used for last-char deletion
        Editor.withoutNormalizing(editor, () => {
          // Relink continuation chain in Slate before removing
          const entry = Array.from(
            Editor.nodes(editor, {
              at: [],
              match: (n: any) => isTag(n) && n.tagId === pendingTagId,
            })
          )[0] as any;

          if (entry) {
            const [node, path] = entry;
            const predecessor = Array.from(
              Editor.nodes(editor, {
                at: [],
                match: (n: any) => isTag(n) && n.continuedAt === pendingTagId,
              })
            )[0] as any;

            const successorId = node.continuedAt;

            if (predecessor && successorId) {
              Transforms.setNodes(editor, { continuedAt: successorId } as any, { at: predecessor[1] });
            } else if (predecessor) {
              Transforms.setNodes(editor, { continuedAt: undefined } as any, { at: predecessor[1] });
            }

            Transforms.removeNodes(editor, { at: path });
          }
        });
      } else {
        // Unwrap tag from Slate (keep text) — used for sidebar delete actions
        removeTag(editor, pendingTagId);
      }
    }

    // For table tags: optimistically update Redux references to remove the tag from cells
    if (isTableTag && pendingTag?.onId && references) {
      const updatedReferences = references.map((ref: any) => {
        if (ref.superId !== pendingTag.onId) return ref;
        const updatedContent = ref.selectionContent?.map((row: any[]) =>
          row.map((cell: any) => {
            if (!cell.tags || !cell.tags.includes(pendingTagId)) return cell;
            return { ...cell, tags: cell.tags.filter((t: string) => t !== pendingTagId) };
          })
        );
        return { ...ref, selectionContent: updatedContent };
      });
      editorDispatch(setReferenceData(updatedReferences));
    }

    deleteTagMutation.mutate(
      { id: pendingTagId, documentSuperId, skipSectionRewrite: true },
      {
        onSuccess: () => {
          onDeleted?.();
        },
      }
    );

    setState({ showModal: false, pendingTagId: null, pendingTag: null, removeCompletely: false });
  }, [state, editor, documentSuperId, deleteTagMutation, onDeleted, references, editorDispatch]);

  const cancelDelete = useCallback(() => {
    setState({ showModal: false, pendingTagId: null, pendingTag: null, removeCompletely: false });
  }, []);

  return {
    showModal: state.showModal,
    pendingTagId: state.pendingTagId,
    isDeleting: deleteTagMutation.isLoading,
    requestDelete,
    requestDeleteById,
    confirmDelete,
    cancelDelete,
  };
}
